/** Approved short conversation starters. No hints, AI or interview-message drafting. */
export const MAX_WARMUPS = 3;
export const DEFAULT_WARMUP_COUNT = 2;
export const WARMUP_BANK = [
  {
    "id": "general-day",
    "group": "General",
    "text": "How's your day been so far?"
  },
  {
    "id": "general-camera",
    "group": "General",
    "text": "Have you been on camera before?"
  },
  {
    "id": "general-shoot",
    "group": "General",
    "text": "Have you done a shoot like this before?"
  },
  {
    "id": "work-busy",
    "group": "At work",
    "text": "Been a busy morning?"
  },
  {
    "id": "work-based",
    "group": "At work",
    "text": "Are you usually based here?"
  },
  {
    "id": "work-team",
    "group": "At work",
    "text": "Have you been with the team long?"
  },
  {
    "id": "location-before",
    "group": "On location",
    "text": "Have you been here before?"
  },
  {
    "id": "location-journey",
    "group": "On location",
    "text": "Did you have far to come today?"
  },
  {
    "id": "location-filmed",
    "group": "On location",
    "text": "Have you filmed here before?"
  },
  {
    "id": "project-today",
    "group": "Around the project",
    "text": "Have you been working on this today?"
  },
  {
    "id": "project-talk",
    "group": "Around the project",
    "text": "Is this something you talk about often?"
  },
  {
    "id": "project-setup",
    "group": "Around the project",
    "text": "Is this the setup you normally use?"
  },
  {
    "id": "event-day",
    "group": "At an event",
    "text": "How's the day going so far?"
  },
  {
    "id": "event-around",
    "group": "At an event",
    "text": "Have you had a chance to look around yet?"
  },
  {
    "id": "event-first",
    "group": "At an event",
    "text": "Is this your first time at this event?"
  }
] as const;
export type WarmupId = typeof WARMUP_BANK[number]['id'];
export type WarmupPurpose = 'brand' | 'testimonial' | 'recruitment' | 'explainer' | 'event' | 'general';

const SETS: Record<WarmupPurpose, readonly WarmupId[]> = {
  general: ['general-day', 'general-camera', 'general-shoot'],
  brand: ['general-day', 'work-based', 'general-camera'],
  testimonial: ['general-day', 'location-before', 'general-camera'],
  recruitment: ['general-day', 'work-based', 'work-team'],
  explainer: ['general-day', 'project-today', 'general-shoot'],
  event: ['event-day', 'event-around', 'event-first'],
};

export function warmupPurpose(purpose = '', talent = ''): WarmupPurpose {
  if (/\b(event|conference|summit|expo|attendee)\b/i.test(purpose)) return 'event';
  if (/\b(testimonial|customer story|case study)\b/i.test(purpose)) return 'testimonial';
  if (/\b(recruitment|recruiting|hiring|careers)\b/i.test(purpose)) return 'recruitment';
  if (/\b(explainer|explain|demonstration|demo|how.to)\b/i.test(purpose)) return 'explainer';
  if (/\b(brand|team|company introduction)\b/i.test(purpose)) return 'brand';
  if (/\b(customer|client|patient)\b/i.test(talent)) return 'testimonial';
  if (/\b(employee|staff|team member|colleague)\b/i.test(talent)) return 'brand';
  return 'general';
}

export function chooseWarmups(purpose = '', talent = '', count = DEFAULT_WARMUP_COUNT): string[] {
  const limit = Math.min(MAX_WARMUPS, Math.max(0, Math.floor(Number.isFinite(count) ? count : DEFAULT_WARMUP_COUNT)));
  return SETS[warmupPurpose(purpose, talent)].slice(0, limit).map(id => WARMUP_BANK.find(q => q.id === id)!.text);
}

export interface WarmupNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: WarmupNode[];
}

export function exceedsWarmupLimit(doc: WarmupNode): boolean {
  return (doc.content ?? []).filter(node => node.type === 'briefField' && node.attrs?.key === 'warmup')
    .reduce((count, node) => count + warmupTexts(node.content ?? []).length, 0) > MAX_WARMUPS;
}

function nodeText(node: WarmupNode): string {
  return node.type === 'text' ? node.text ?? '' : (node.content ?? []).map(nodeText).join('');
}

/** Same paragraph/list-item convention as interview questions; nested cues are ignored. */
export function warmupTexts(content: readonly WarmupNode[]): string[] {
  const texts: string[] = [];
  for (const node of content) {
    if (node.type === 'paragraph') {
      const text = nodeText(node).trim();
      if (text) texts.push(text);
    } else if (node.type === 'bulletList' || node.type === 'orderedList') {
      for (const item of node.content ?? []) {
        const text = (item.content ?? []).filter(child => child.type !== 'bulletList' && child.type !== 'orderedList').map(nodeText).join(' ').trim();
        if (text) texts.push(text);
      }
    }
  }
  return texts;
}

export function warmupBody(texts: readonly string[]): WarmupNode[] {
  const selected = texts.map(text => text.trim()).filter(Boolean).slice(0, MAX_WARMUPS);
  return selected.length ? [{ type: 'bulletList', content: selected.map(text => ({
    type: 'listItem', content: [{ type: 'paragraph', content: [{ type: 'text', text }] }],
  })) }] : [{ type: 'paragraph' }];
}
